'use client';

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

interface EditorPrefs {
    wordWrap: boolean;
    minimap: boolean;
    fontSize: number;
}

interface EditorPrefsContextValue extends EditorPrefs {
    toggleWordWrap: () => void;
    toggleMinimap: () => void;
    increaseFontSize: () => void;
    decreaseFontSize: () => void;
}

const STORAGE_KEY = 'editor-prefs';
const MIN_FONT_SIZE = 10;
const MAX_FONT_SIZE = 24;
const DEFAULT_PREFS: EditorPrefs = { wordWrap: false, minimap: false, fontSize: 14 };

const EditorPrefsContext = createContext<EditorPrefsContextValue | undefined>(undefined);

export function EditorPrefsProvider({ children }: { children: React.ReactNode }) {
    const [prefs, setPrefs] = useState<EditorPrefs>(DEFAULT_PREFS);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored);
                setPrefs((prev) => ({ ...prev, ...parsed }));
            }
        } catch {
            // ignore - localStorage unavailable or malformed
        }
    }, []);

    const persist = useCallback((next: EditorPrefs) => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
            // ignore
        }
    }, []);

    const toggleWordWrap = useCallback(() => {
        setPrefs((prev) => {
            const next = { ...prev, wordWrap: !prev.wordWrap };
            persist(next);
            return next;
        });
    }, [persist]);

    const toggleMinimap = useCallback(() => {
        setPrefs((prev) => {
            const next = { ...prev, minimap: !prev.minimap };
            persist(next);
            return next;
        });
    }, [persist]);

    const increaseFontSize = useCallback(() => {
        setPrefs((prev) => {
            const next = { ...prev, fontSize: Math.min(MAX_FONT_SIZE, prev.fontSize + 1) };
            persist(next);
            return next;
        });
    }, [persist]);

    const decreaseFontSize = useCallback(() => {
        setPrefs((prev) => {
            const next = { ...prev, fontSize: Math.max(MIN_FONT_SIZE, prev.fontSize - 1) };
            persist(next);
            return next;
        });
    }, [persist]);

    return (
        <EditorPrefsContext.Provider
            value={{ ...prefs, toggleWordWrap, toggleMinimap, increaseFontSize, decreaseFontSize }}
        >
            {children}
        </EditorPrefsContext.Provider>
    );
}

export function useEditorPrefs() {
    const ctx = useContext(EditorPrefsContext);
    if (!ctx) {
        throw new Error('useEditorPrefs must be used within an EditorPrefsProvider');
    }
    return ctx;
}
